import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

import ProjectReadinessConfig from "./ProjectReadiness.config";
import ProjectReadinessSchema from "./ProjectReadiness.schema";
import { generateProjectReadinessSummary } from "../../../services/projectReadinessApi";
import { EVENT_NAMES, PARAM_KEYS, trackEvent } from "../../../analytics";

import "../../../assets/stylesheets/toolBase.css";

/**
 * ProjectReadinessTool
 *
 * Helps users evaluate readiness to begin a home improvement project.
 * Identifies gaps and recommends next steps.
 */
const ProjectReadinessTool = ({ category }) => {
  const {
    title,
    description,
    defaults,
    ctaLabel,
    loadingLabel,
    loadingMessage,
    projectTypeOptions,
    budgetRangeOptions,
    timelineOptions,
    urgencyOptions,
    projectSizeOptions,
    contractorReadinessOptions,
    result,
    readinessLabels,
  } = ProjectReadinessConfig;

  const [responses, setResponses] = useState({ ...defaults });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [summaryResult, setSummaryResult] = useState(null);

  const shouldShowCta = useMemo(() => {
    if (!summaryResult?.suggestedCategory?.slug) {
      return false;
    }

    return ["urgent", "ready", "planning"].includes(
      summaryResult.readinessLevel
    );
  }, [summaryResult]);

  const handleToggle = (field) => {
    setResponses((prev) => ({
      ...prev,
      [field]: !prev[field],
      ...(field === "hasBudgetRange" && prev[field]
        ? { budgetRange: "" }
        : {}),
      ...(field === "hasTimeline" && prev[field]
        ? { timeline: "" }
        : {}),
    }));

    setErrors((prev) => {
      const nextErrors = { ...prev };

      if (field === "hasBudgetRange") {
        delete nextErrors.budgetRange;
      }

      if (field === "hasTimeline") {
        delete nextErrors.timeline;
      }

      return nextErrors;
    });
  };

  const handleChange = (field, value) => {
    setResponses((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }

      const nextErrors = { ...prev };
      delete nextErrors[field];
      return nextErrors;
    });
  };

  const validate = () => {
    const nextErrors = {};
    const trimmedGoal = responses.projectGoal.trim();
    const minProjectGoalLength =
      ProjectReadinessSchema.validation.minProjectGoalLength;

    if (!responses.projectType) {
      nextErrors.projectType = "Please select the type of project you are considering.";
    }

    if (trimmedGoal.length < minProjectGoalLength) {
      nextErrors.projectGoal = `Please describe your project in at least ${minProjectGoalLength} characters.`;
    }

    if (responses.hasBudgetRange && !responses.budgetRange) {
      nextErrors.budgetRange = "Please choose your budget range.";
    }

    if (responses.hasTimeline && !responses.timeline) {
      nextErrors.timeline = "Please choose your preferred timeline.";
    }

    if (!responses.urgency) {
      nextErrors.urgency = "Please select how urgent this project feels.";
    }

    if (!responses.projectSize) {
      nextErrors.projectSize = "Please select the size of the project.";
    }

    if (!responses.contractorReadiness) {
      nextErrors.contractorReadiness = "Please select whether you are ready to speak with a contractor.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setApiError("");

    if (!validate()) {
      return;
    }

    const payload = {
      projectType: responses.projectType,
      projectGoal: responses.projectGoal.trim(),
      hasBudgetRange: responses.hasBudgetRange,
      budgetRange: responses.hasBudgetRange ? responses.budgetRange : null,
      hasTimeline: responses.hasTimeline,
      timeline: responses.hasTimeline ? responses.timeline : null,
      urgency: responses.urgency,
      projectSize: responses.projectSize,
      contractorReadiness: responses.contractorReadiness,
    };

    trackEvent(EVENT_NAMES.AI_REPORT_GENERATE_STARTED, {
      [PARAM_KEYS.CATEGORY_KEY]: category,
      [PARAM_KEYS.PLACEMENT]: "project_readiness_tool",
      [PARAM_KEYS.REPORT_TYPE]: "project_readiness_summary",
    });

    setLoading(true);

    try {
      const nextSummary = await generateProjectReadinessSummary(payload);
      setSummaryResult(nextSummary);

      trackEvent(EVENT_NAMES.AI_REPORT_GENERATED, {
        [PARAM_KEYS.CATEGORY_KEY]: nextSummary?.suggestedCategory?.slug || category,
        [PARAM_KEYS.PLACEMENT]: "project_readiness_tool",
        [PARAM_KEYS.REPORT_TYPE]: "project_readiness_summary",
        [PARAM_KEYS.STATUS]: nextSummary?.readinessLevel,
      });
    } catch (err) {
      setApiError(
        err?.message ||
          "We couldn’t generate your readiness summary right now. Please try again in a moment."
      );

      if (err?.details && typeof err.details === "object") {
        setErrors((prev) => ({ ...prev, ...err.details }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSurveyCtaClick = () => {
    if (!summaryResult?.suggestedCategory?.slug) {
      return;
    }

    trackEvent(EVENT_NAMES.CTA_CLICK, {
      [PARAM_KEYS.CTA_NAME]: "start_project_readiness_survey",
      [PARAM_KEYS.CATEGORY_KEY]: summaryResult.suggestedCategory.slug,
      [PARAM_KEYS.PLACEMENT]: "project_readiness_results",
      [PARAM_KEYS.REPORT_TYPE]: "project_readiness_summary",
      [PARAM_KEYS.STATUS]: summaryResult.readinessLevel,
    });
  };

  return (
    <div className="tool project-readiness-tool">
      <header className="tool-header">
        <h1 className="tool-title">{title}</h1>
        <p className="tool-description">{description}</p>
      </header>

      <form className="project-readiness-form" onSubmit={handleSubmit} noValidate>
        <div className="tool-section">
          <strong>Project Readiness Questions</strong>

          <div className="tool-form-row">
            <label className="tool-label" htmlFor="projectType">
              What type of project are you considering?
            </label>
            <select
              id="projectType"
              className="tool-select"
              value={responses.projectType}
              onChange={(e) => handleChange("projectType", e.target.value)}
            >
              <option value="">Select a project type</option>
              {projectTypeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.projectType && (
              <span className="tool-field-error">{errors.projectType}</span>
            )}
          </div>

          <div className="tool-form-row">
            <label className="tool-label" htmlFor="projectGoal">
              What is the main problem or goal you want to solve?
            </label>
            <textarea
              id="projectGoal"
              className="tool-textarea"
              rows="4"
              maxLength={ProjectReadinessSchema.validation.maxProjectGoalLength}
              placeholder="Briefly describe what you want done, what problem you are seeing, or what outcome you want."
              value={responses.projectGoal}
              onChange={(e) => handleChange("projectGoal", e.target.value)}
            />
            {errors.projectGoal && (
              <span className="tool-field-error">{errors.projectGoal}</span>
            )}
          </div>

          <div className="tool-form-row">
            <label className="tool-label project-readiness-checkbox">
              <input
                type="checkbox"
                checked={responses.hasBudgetRange}
                onChange={() => handleToggle("hasBudgetRange")}
              />
              <span>I have a general budget range in mind.</span>
            </label>

            {responses.hasBudgetRange && (
              <div className="project-readiness-reveal">
                <label className="tool-label" htmlFor="budgetRange">
                  Budget range
                </label>
                <select
                  id="budgetRange"
                  className="tool-select"
                  value={responses.budgetRange}
                  onChange={(e) => handleChange("budgetRange", e.target.value)}
                >
                  <option value="">Select a budget range</option>
                  {budgetRangeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {errors.budgetRange && (
                  <span className="tool-field-error">{errors.budgetRange}</span>
                )}
              </div>
            )}
          </div>

          <div className="tool-form-row">
            <label className="tool-label project-readiness-checkbox">
              <input
                type="checkbox"
                checked={responses.hasTimeline}
                onChange={() => handleToggle("hasTimeline")}
              />
              <span>I know roughly when I want this project completed.</span>
            </label>

            {responses.hasTimeline && (
              <div className="project-readiness-reveal">
                <label className="tool-label" htmlFor="timeline">
                  Preferred timeline
                </label>
                <select
                  id="timeline"
                  className="tool-select"
                  value={responses.timeline}
                  onChange={(e) => handleChange("timeline", e.target.value)}
                >
                  <option value="">Select a timeline</option>
                  {timelineOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {errors.timeline && (
                  <span className="tool-field-error">{errors.timeline}</span>
                )}
              </div>
            )}
          </div>

          <div className="tool-form-row">
            <label className="tool-label" htmlFor="urgency">
              How serious or urgent is this project?
            </label>
            <select
              id="urgency"
              className="tool-select"
              value={responses.urgency}
              onChange={(e) => handleChange("urgency", e.target.value)}
            >
              <option value="">Select urgency</option>
              {urgencyOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.urgency && (
              <span className="tool-field-error">{errors.urgency}</span>
            )}
          </div>

          <div className="tool-form-row">
            <label className="tool-label" htmlFor="projectSize">
              How large is the project?
            </label>
            <select
              id="projectSize"
              className="tool-select"
              value={responses.projectSize}
              onChange={(e) => handleChange("projectSize", e.target.value)}
            >
              <option value="">Select project size</option>
              {projectSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.projectSize && (
              <span className="tool-field-error">{errors.projectSize}</span>
            )}
          </div>

          <div className="tool-form-row">
            <label className="tool-label" htmlFor="contractorReadiness">
              Are you ready to speak with a contractor if the project appears feasible?
            </label>
            <select
              id="contractorReadiness"
              className="tool-select"
              value={responses.contractorReadiness}
              onChange={(e) => handleChange("contractorReadiness", e.target.value)}
            >
              <option value="">Select an option</option>
              {contractorReadinessOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.contractorReadiness && (
              <span className="tool-field-error">{errors.contractorReadiness}</span>
            )}
          </div>

          {category && (
            <p className="tool-muted project-readiness-context">
              You opened this tool from the {category} category. Your summary may still suggest a different category if your answers point elsewhere.
            </p>
          )}
        </div>

        <div className="tool-actions">
          <button
            type="submit"
            className="tool-button-primary"
            disabled={loading}
          >
            {loading ? loadingLabel : ctaLabel}
          </button>
        </div>

        {loading && <p className="tool-muted">{loadingMessage}</p>}
        {apiError && <p className="tool-field-error">{apiError}</p>}
      </form>

      {summaryResult && (
        <section className="tool-results">
          <div className="tool-result-card">
            <strong>{result.title}</strong>
            <p className="tool-muted">{result.disclaimer}</p>
            <div className="project-readiness-badge-row">
              <span className="project-readiness-badge">
                Readiness Level: {readinessLabels[summaryResult.readinessLevel] || summaryResult.readinessLevel}
              </span>
              <span className="project-readiness-confidence">
                Suggested category confidence: {summaryResult.suggestedCategory?.confidence || "low"}
              </span>
            </div>
            <p className="project-readiness-summary-copy">
              {summaryResult.summary}
            </p>
          </div>

          <div className="tool-result-card">
            <strong>Recommended Next Steps</strong>
            <ul className="project-readiness-list">
              {summaryResult.nextSteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          </div>

          <div className="tool-result-card">
            <strong>Missing Information</strong>
            {summaryResult.missingInformation.length > 0 ? (
              <ul className="project-readiness-list">
                {summaryResult.missingInformation.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : (
              <p className="tool-muted">You have covered the core planning details for this project.</p>
            )}
          </div>

          <div className="tool-result-card">
            <strong>Suggested Category</strong>
            <p className="tool-muted">
              {summaryResult.suggestedCategory?.label || "No category suggested yet"}
            </p>

            {shouldShowCta ? (
              <div className="tool-actions project-readiness-results-actions">
                <Link
                  className="tool-button-primary project-readiness-cta-link"
                  to={`/survey/${summaryResult.suggestedCategory.slug}`}
                  onClick={handleSurveyCtaClick}
                >
                  {summaryResult.ctaText}
                </Link>
              </div>
            ) : (
              <p className="tool-muted">
                Keep refining your project details before moving into the full category survey.
              </p>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

ProjectReadinessTool.propTypes = {
  category: PropTypes.string,
};

export default ProjectReadinessTool;
